import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  SelectChangeEvent,
  Typography,
  Chip,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  RestartAlt as ResetIcon,
  Search as SearchIcon,
  LocalOffer as KeywordsIcon,
  Psychology as AiIndexIcon,
  HelpOutline as HelpIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { TranslationStatus } from '@components/common/TranslationStatus.tsx';
import {
  adminMetActivityService,
  MetActivityDetail,
  MetIndexBuildState,
  MetIndexRowStatus,
} from '@services/admin-met-activity.service.ts';
import {
  MetActivity,
  MetActivityOptions,
  MetCompendiumType,
  MetSourceType,
  MetSortBy,
  CreateMetActivityDto,
  splitKeywords,
} from '@services/met-activity.service.ts';
import { MetActivityKeywordsDialog } from '@components/admin/lib/MetActivityKeywordsDialog.tsx';
import { MetAiSearchExplanationDialog } from '@components/admin/lib/MetAiSearchExplanationDialog.tsx';
import { useDebounce } from '@hooks/useDebounce.ts';
import { toastService } from '@services/toast.service.ts';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { MetActivityDialog } from '@components/admin/lib/MetActivityDialog.tsx';
import { MetActivityDetailDialog } from '@components/admin/lib/MetActivityDetailDialog.tsx';
import { LibraryTable, Column } from '@components/admin/lib/LibraryTable.tsx';

const STORAGE_KEY = 'admin_met_activity_filters';

const COMPENDIUM_COLORS: Record<string, 'primary' | 'secondary' | 'warning'> = {
  adult: 'primary',
  elderly: 'secondary',
  youth: 'warning',
};

const INDEX_STATUS_VIEW: Record<Exclude<MetIndexRowStatus, 'not_indexed'>, { label: string; color: 'success' | 'warning' | 'error'; hint: string }> = {
  actual: { label: 'Актуальний', color: 'success', hint: 'AI шукає цей запис за поточною назвою, перекладами й ключовими фразами' },
  stale: { label: 'Застарів', color: 'warning', hint: 'Текст змінився після побудови - AI ще шукає за старим. Перерахуйте' },
  missing: { label: 'Немає', color: 'error', hint: 'Запису ще немає в AI-індексі - AI його не знайде. Перерахуйте' },
};

export const AdminMetActivitiesTab: React.FC = () => {
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedState = getInitialState();

  const [activities, setActivities] = useState<MetActivity[]>([]);
  const [keywordsActivity, setKeywordsActivity] = useState<MetActivity | null>(null);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [indexBuild, setIndexBuild] = useState<MetIndexBuildState | null>(null);
  const rebuildingIndex = !!indexBuild?.running;
  // Статус AI-індексу рядків поточної сторінки (id → actual / stale / missing / not_indexed).
  const [indexStatus, setIndexStatus] = useState<Record<number, MetIndexRowStatus>>({});

  // Перебудова йде у фоні на сервері: запуск, далі опитування стану до завершення.
  const handleRebuildAiIndex = async () => {
    try {
      const { started, build } = await adminMetActivityService.rebuildAiIndex();
      setIndexBuild(build);
      if (!started) toastService.info('AI-індекс уже оновлюється - показую прогрес');
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || 'Не вдалося запустити оновлення AI-індексу');
    }
  };

  const loadIndexStatus = useCallback(async (ids: number[]) => {
    try {
      const status = await adminMetActivityService.getAiIndexStatus(ids);
      setIndexStatus(prev => ({ ...prev, ...status }));
    } catch {
      // статус - допоміжний, таблиця працює й без нього
    }
  }, []);

  useEffect(() => {
    loadIndexStatus(activities.map(a => a.id));
  }, [activities, loadIndexStatus]);

  const [rebuildingRowId, setRebuildingRowId] = useState<number | null>(null);
  const handleRebuildRow = async (activity: MetActivity) => {
    setRebuildingRowId(activity.id);
    try {
      const status = await adminMetActivityService.rebuildAiIndexOne(activity.id);
      setIndexStatus(prev => ({ ...prev, [activity.id]: status }));
      toastService.success(`AI-індекс оновлено: ${activity.code}`);
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || 'Не вдалося перерахувати AI-індекс запису');
    } finally {
      setRebuildingRowId(null);
    }
  };

  useEffect(() => {
    if (!indexBuild?.running) return;
    const timer = setInterval(async () => {
      try {
        const { build } = await adminMetActivityService.getAiIndexStats();
        setIndexBuild(build);
        if (!build.running) {
          if (build.error || build.failed) {
            toastService.error(`AI-індекс: ${build.error ?? `не вдалося обробити ${build.failed} записів`} (оновлено ${build.built}). Спробуйте ще раз пізніше.`);
          } else {
            toastService.success(build.built ? `AI-індекс оновлено: ${build.built} записів` : 'AI-індекс актуальний');
          }
          loadIndexStatus(activities.map(a => a.id));
        }
      } catch {
        // збій опитування - наступний тік
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [indexBuild?.running, activities, loadIndexStatus]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(savedState?.page || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(20);

  const [searchQuery, setSearchQuery] = useState(savedState?.searchQuery || '');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [sourceTypeFilter, setSourceTypeFilter] = useState<MetSourceType>(
    savedState?.sourceTypeFilter || 'all'
  );
  const [compendiumTypeFilter, setCompendiumTypeFilter] = useState<MetCompendiumType | ''>(
    savedState?.compendiumTypeFilter || ''
  );
  const [categoryCodeFilter, setCategoryCodeFilter] = useState<number | ''>(
    savedState?.categoryCodeFilter ?? ''
  );

  const [sortBy, setSortBy] = useState<MetSortBy>(savedState?.sortBy || 'id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(savedState?.sortOrder || 'ASC');

  const [options, setOptions] = useState<MetActivityOptions>({
    compendiumTypes: [],
    categories: [],
    youthAgeGroups: [],
    sourceTypes: [],
  });

  // Create modal
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);

  // Edit modal
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [activityToEdit, setActivityToEdit] = useState<MetActivity | null>(null);
  const [editData, setEditData] = useState<any>(null);

  // Detail modal
  const [isDetailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState<MetActivityDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete confirmation
  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<MetActivity | null>(null);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      page,
      searchQuery,
      sourceTypeFilter,
      compendiumTypeFilter,
      categoryCodeFilter,
      sortBy,
      sortOrder,
    }));
  }, [page, searchQuery, sourceTypeFilter, compendiumTypeFilter, categoryCodeFilter, sortBy, sortOrder]);

  // Fetch options on mount
  useEffect(() => {
    adminMetActivityService.getOptions()
      .then(setOptions)
      .catch(() => toastService.error('Не вдалося завантажити параметри фільтрів.'));
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminMetActivityService.getActivities({
        page,
        limit,
        search: debouncedSearchQuery || undefined,
        sourceType: sourceTypeFilter || undefined,
        compendiumType: compendiumTypeFilter || undefined,
        categoryCode: categoryCodeFilter !== '' ? (categoryCodeFilter as number) : undefined,
        sortBy,
        sortOrder,
      });
      setActivities(response.data);
      setTotalPages(
        response.meta?.total && response.meta?.limit
          ? Math.ceil(response.meta.total / response.meta.limit)
          : 0
      );
    } catch {
      toastService.error('Не вдалося завантажити список активностей.');
      setError('Не вдалося завантажити список активностей.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearchQuery, sourceTypeFilter, compendiumTypeFilter, categoryCodeFilter, sortBy, sortOrder]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handlePageChange = (value: number) => setPage(value);
  const handleLimitChange = (value: number) => { setLimit(value); setPage(1); };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleSourceTypeFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setSourceTypeFilter(event.target.value as MetSourceType);
    setPage(1);
  }, []);

  const handleCompendiumFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setCompendiumTypeFilter(event.target.value as MetCompendiumType | '');
    setPage(1);
  }, []);

  const handleCategoryFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setCategoryCodeFilter(event.target.value !== '' ? Number(event.target.value) : '');
    setPage(1);
  }, []);

  const handleResetFilters = () => {
    setSearchQuery('');
    setSourceTypeFilter('all');
    setCompendiumTypeFilter('');
    setCategoryCodeFilter('');
    setPage(1);
    setSortBy('id');
    setSortOrder('ASC');
  };

  const handleSort = (property: string) => {
    const isAsc = sortBy === property && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortBy(property as MetSortBy);
  };

  // Create
  const handleCreateActivity = async (data: any) => {
    setLoading(true);
    try {
      const payload: CreateMetActivityDto = {
        code: data.code,
        compendiumType: data.compendiumType,
        categoryCode: data.categoryCode,
        categoryName: data.categoryName,
        nameEn: data.nameEn,
        nameI18n: data.nameI18n,
        metValue: data.metValue,
        youthAgeGroup: data.youthAgeGroup,
      };
      await adminMetActivityService.createActivity(payload);
      toastService.success('Активність успішно створена!');
      setCreateModalOpen(false);
      fetchActivities();
    } catch (err: any) {
      toastService.error(`Помилка створення: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  // Edit
  const handleOpenEditModal = async (activity: MetActivity) => {
    setLoading(true);
    setActivityToEdit(activity);
    try {
      const data = await adminMetActivityService.getActivityForEdit(activity.id);
      setEditData(data);
      setEditModalOpen(true);
    } catch {
      toastService.error('Не вдалося завантажити дані для редагування.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateActivity = async (data: any) => {
    if (!activityToEdit) return;
    setLoading(true);
    try {
      const payload: Partial<CreateMetActivityDto> = {
        code: data.code,
        compendiumType: data.compendiumType,
        categoryCode: data.categoryCode,
        categoryName: data.categoryName,
        nameEn: data.nameEn,
        nameI18n: data.nameI18n,
        metValue: data.metValue,
        youthAgeGroup: data.youthAgeGroup,
      };
      await adminMetActivityService.updateActivity(activityToEdit.id, payload);
      toastService.success('Активність успішно оновлена!');
      setEditModalOpen(false);
      setActivityToEdit(null);
      fetchActivities();
    } catch (err: any) {
      toastService.error(`Помилка оновлення: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  // Detail
  const handleOpenDetailModal = async (activity: MetActivity) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    try {
      const data = await adminMetActivityService.getActivityDetail(activity.id);
      setDetailData(data);
    } catch {
      toastService.error('Не вдалося завантажити деталі активності.');
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditFromDetail = () => {
    if (detailData) {
      setDetailModalOpen(false);
      handleOpenEditModal({ id: detailData.id } as MetActivity);
    }
  };

  // Delete
  const handleOpenDeleteConfirm = (activity: MetActivity) => {
    setActivityToDelete(activity);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteActivity = async () => {
    if (!activityToDelete) return;
    setLoading(true);
    try {
      await adminMetActivityService.deleteActivity(activityToDelete.id);
      toastService.success(`Активність "${activityToDelete.nameEn}" видалена.`);
      setConfirmDeleteOpen(false);
      setActivityToDelete(null);
      fetchActivities();
    } catch (err: any) {
      toastService.error(`Помилка видалення: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const columns: Column<MetActivity>[] = [
    {
      id: 'code',
      label: 'Код',
      sortable: true,
      render: (activity) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
          {activity.code}
        </Typography>
      ),
    },
    {
      id: 'compendiumType',
      label: 'Компендіум',
      render: (activity) => (
        <Chip
          label={activity.compendiumType}
          size="small"
          color={COMPENDIUM_COLORS[activity.compendiumType] ?? 'default'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'categoryCode',
      label: 'Категорія',
      sortable: true,
      render: (activity) => (
        <Box>
          <Typography variant="body2">{activity.categoryName}</Typography>
          <Typography variant="caption" color="text.secondary">[{activity.categoryCode}]</Typography>
        </Box>
      ),
    },
    {
      id: 'nameEn',
      label: 'Назва',
      sortable: true,
      render: (activity) => (
        <Stack spacing={0.5}>
          <Typography variant="body2">{activity.name}</Typography>
          {activity.name !== activity.nameEn && (
            <Typography variant="caption" color="text.secondary">{activity.nameEn}</Typography>
          )}
          <TranslationStatus availableTranslations={activity.availableTranslations} />
        </Stack>
      ),
    },
    {
      id: 'metValue',
      label: 'MET',
      sortable: true,
      render: (activity) => (
        <Typography variant="body2" fontWeight="bold">{activity.metValue}</Typography>
      ),
    },
    {
      id: 'keywords',
      label: 'Ключові фрази',
      render: (activity) => {
        const keywords = splitKeywords(activity.keywords);
        if (!keywords.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Tooltip title={keywords.join(', ')}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 220 }}>
              {keywords.slice(0, 3).map(k => <Chip key={k} label={k} size="small" />)}
              {keywords.length > 3 && <Chip label={`+${keywords.length - 3}`} size="small" variant="outlined" />}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      id: 'aiIndex',
      label: 'AI-індекс',
      render: (activity) => {
        const status = indexStatus[activity.id];
        if (!status) return <Typography variant="body2" color="text.secondary">…</Typography>;
        if (status === 'not_indexed') {
          return (
            <Tooltip title="Не індексується: AI шукає лише в офіційних записах Compendium для дорослих">
              <Typography variant="body2" color="text.secondary">—</Typography>
            </Tooltip>
          );
        }
        const view = INDEX_STATUS_VIEW[status];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title={view.hint}>
              <Chip label={view.label} size="small" color={view.color} variant="outlined" />
            </Tooltip>
            <Tooltip title="Перерахувати AI-індекс цього запису">
              <span>
                <IconButton size="small" onClick={() => handleRebuildRow(activity)} disabled={rebuildingRowId === activity.id || rebuildingIndex}>
                  {rebuildingRowId === activity.id ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: 'isSystem',
      label: 'Джерело',
      render: (activity) => (
        <Typography variant="body2">
          {activity.isSystem ? 'Система' : 'Користувач'}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateModalOpen(true)} disabled>
          Додати активність
        </Button>
        <Tooltip title="Перша побудова AI-індексу або дозбирання пропущених записів. Після зміни запису його індекс оновлюється автоматично">
          <Button variant="outlined" startIcon={<AiIndexIcon />} onClick={handleRebuildAiIndex} disabled={rebuildingIndex}>
            {rebuildingIndex ? `Оновлення… ${indexBuild?.processed ?? 0}/${indexBuild?.total || '…'}` : 'Оновити AI-індекс'}
          </Button>
        </Tooltip>
        <Button variant="text" startIcon={<HelpIcon />} onClick={() => setExplanationOpen(true)}>
          Як AI шукає в базі MET
        </Button>
      </Box>

      {/* Filter Panel */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Пошук"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 2, minWidth: '200px' }}
          size="small"
        />

        <FormControl sx={{ minWidth: '140px', flexGrow: 1 }} size="small">
          <InputLabel>Джерело</InputLabel>
          <Select value={sourceTypeFilter} label="Джерело" onChange={handleSourceTypeFilterChange}>
            {options.sourceTypes.map(t => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: '150px', flexGrow: 1 }} size="small">
          <InputLabel>Компендіум</InputLabel>
          <Select value={compendiumTypeFilter} label="Компендіум" onChange={handleCompendiumFilterChange}>
            <MenuItem value=""><em>Всі</em></MenuItem>
            {options.compendiumTypes.map(t => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: '180px', flexGrow: 1 }} size="small">
          <InputLabel>Категорія</InputLabel>
          <Select value={categoryCodeFilter === '' ? '' : String(categoryCodeFilter)} label="Категорія" onChange={handleCategoryFilterChange}>
            <MenuItem value=""><em>Всі</em></MenuItem>
            {options.categories.map(c => (
              <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Скинути всі фільтри">
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
            startIcon={<ResetIcon />}
            sx={{ height: '40px' }}
          >
            Скинути
          </Button>
        </Tooltip>
      </Box>

      <LibraryTable
        extraActions={(activity) => (
          <Tooltip title="Ключові фрази">
            <IconButton size="small" onClick={() => setKeywordsActivity(activity)}>
              <KeywordsIcon />
            </IconButton>
          </Tooltip>
        )}
        data={activities}
        columns={columns}
        loading={loading}
        error={error}
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        limit={limit}
        onLimitChange={handleLimitChange}
        storageKey="pagination:admin-met-activities"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onEdit={handleOpenEditModal}
        onDelete={handleOpenDeleteConfirm}
        onRowClick={handleOpenDetailModal}
        isUserCreated={() => false}
        disableDelete
      />

      <MetActivityDialog
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateActivity}
        options={options}
        isLoading={loading}
        title="Додати нову активність"
        submitLabel="Створити"
      />

      <MetActivityDialog
        open={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateActivity}
        initialData={editData}
        options={options}
        isLoading={loading}
        title="Редагувати активність"
        submitLabel="Зберегти зміни"
      />

      <MetAiSearchExplanationDialog open={explanationOpen} onClose={() => setExplanationOpen(false)} />

      <MetActivityKeywordsDialog
        activity={keywordsActivity}
        onClose={() => setKeywordsActivity(null)}
        onSaved={(updated) => {
          setActivities(prev => prev.map(a => (a.id === updated.id ? { ...a, keywords: updated.keywords } : a)));
          // Вектор запису оновлюється на сервері у фоні (кілька секунд) - тоді й оновлюємо статус.
          setTimeout(() => loadIndexStatus([updated.id]), 4000);
        }}
      />

      <MetActivityDetailDialog
        open={isDetailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        onEdit={handleEditFromDetail}
        data={detailData}
        isLoading={detailLoading}
      />

      <ConfirmationDialog
        open={isConfirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteActivity}
        title="Підтвердити видалення"
        message={`Ви впевнені, що хочете видалити активність "${activityToDelete?.nameEn}"?`}
        confirmText="Видалити"
      />
    </Box>
  );
};
